import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { useDispatch } from "react-redux";
import { fetchMyOrderTimeline } from "../_redux/order/orderAction";
import {
  getStoreProtectStatus,
  storeProtectPolicyDownloadUrl,
  uploadStoreProtectPolicy,
} from "../_redux/order/storeProtectCrud";
import downloadExport from "../../../utils/downloadExport";

const MAX_BYTES = 10 * 1024 * 1024;

/**
 * The customer's own insurance policy, on a container order without Store
 * Protect. Staff upload the policy here when the customer sends it to them
 * instead of using the emailed upload link. The server runs the same
 * automatic check as the customer's upload; if the policy fails it, the
 * reasons are shown and staff can accept it anyway with a reason.
 *
 * Renders nothing for orders that cannot take a policy and have none.
 *
 * Mount it with `key` set to the order id, so nothing carries over when staff
 * move to another order; an upload answered after that is ignored.
 *
 * @param {Object} props
 * @param {number|string} props.orderId The order.
 * @returns {JSX.Element|null}
 */
export function StoreProtectPolicyPanel({ orderId }) {
  const dispatch = useDispatch();
  const [status, setStatus] = useState(null);
  const [reload, setReload] = useState(0);
  const [file, setFile] = useState(null);
  const [expiryDate, setExpiryDate] = useState("");
  const [reason, setReason] = useState("");
  const [refusal, setRefusal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  // The order this panel is showing, so a reply to an upload started for
  // another order, or arriving after the panel has gone, is ignored.
  // Updated in an effect, not during render (react-hooks/refs).
  const activeOrder = useRef(orderId);
  const mounted = useRef(true);

  useEffect(() => {
    activeOrder.current = orderId;
  }, [orderId]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!orderId) return undefined;
    let current = true;

    getStoreProtectStatus(orderId)
      .then((res) => {
        if (current) setStatus(res?.data?.data || null);
      })
      .catch(() => {
        if (current) setStatus(null);
      });

    return () => {
      current = false;
    };
  }, [orderId, reload]);

  /**
   * Keep the chosen file if it is small enough, and start a fresh attempt.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event
   * @returns {void}
   */
  const handleFile = (event) => {
    const picked = event.target.files?.[0] || null;
    setRefusal(null);
    setReason("");
    // A date typed for the last file must not be sent with this one: the
    // server prefers it over the date read from the policy.
    setExpiryDate("");
    if (picked && picked.size > MAX_BYTES) {
      // Clear the input too, or it keeps showing the rejected file's name
      // and the same file cannot be picked again.
      event.target.value = "";
      setFile(null);
      setMessage({ type: "error", text: "That file is over 10 MB. Please choose a smaller one." });
      return;
    }
    setFile(picked);
    setMessage({ type: "", text: "" });
  };

  /**
   * Send the policy for checking and upload.
   *
   * When an override is answered with a request for the expiry date, the
   * override stays on offer with the reason kept, so staff add the date and
   * press "Upload anyway" again instead of re-running the check.
   *
   * @param {boolean} override Accept the policy although the check did not pass.
   * @returns {void}
   */
  const submit = (override) => {
    if (!file || busy) return;
    if (override && !reason.trim()) {
      setMessage({ type: "error", text: "Say why you are accepting this policy." });
      return;
    }

    setBusy(true);
    setMessage({ type: "", text: "" });
    const requestOrder = orderId;
    /** Whether the panel is still showing the order this upload was for. */
    const stillHere = () => mounted.current && activeOrder.current === requestOrder;
    uploadStoreProtectPolicy(orderId, {
      file,
      expiryDate,
      override,
      overrideReason: reason.trim(),
    })
      .then((res) => {
        if (!stillHere()) return;
        const overridden = res?.data?.data?.overridden;
        setMessage({
          type: "success",
          text: overridden
            ? "Policy uploaded. The check did not pass and your reason has been recorded on the order."
            : "Policy uploaded. It passed the automatic check.",
        });
        setFile(null);
        setExpiryDate("");
        setReason("");
        setRefusal(null);
        setReload((n) => n + 1);
        dispatch(fetchMyOrderTimeline(requestOrder));
      })
      .catch((err) => {
        if (!stillHere()) return;
        const body = err?.response?.data || {};
        const data = body.data || {};
        if (err?.response?.status === 422 && (data.can_override || data.needs_expiry_date)) {
          // The expiry-date reply carries no can_override; keep the override
          // when it answers an override attempt.
          setRefusal((prev) => (override && prev?.can_override ? { ...prev, ...data } : data));
        }
        setMessage({
          type: "error",
          text:
            body.errors?.file?.[0] ||
            body.errors?.expiry_date?.[0] ||
            body.message ||
            "The policy could not be uploaded. Please try again.",
        });
      })
      .finally(() => {
        if (stillHere()) setBusy(false);
      });
  };

  if (!orderId || !status || !status.is_container) return null;

  const policies = status.policies || [];
  if (!status.can_upload && policies.length === 0) return null;

  const check = refusal?.check;
  const read = check?.read_from_policy;

  return (
    <div className="card card-custom gutter-b">
      <div className="card-header align-items-center border-0 mt-4 mb-0">
        <h3 className="card-title align-items-start flex-column">
          <span className="font-weight-bolder text-dark">
            Customer&apos;s own insurance policy
          </span>
        </h3>
      </div>
      <div className="card-body pt-0">
        {policies.length === 0 ? (
          <div className="text-muted mb-3">No policy has been uploaded for this order.</div>
        ) : (
          <div className="mb-3">
            {policies.map((policy) => (
              <div key={policy.id}>
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0"
                  onClick={() =>
                    downloadExport(storeProtectPolicyDownloadUrl(orderId, policy.id), (text) =>
                      setMessage({ type: "error", text })
                    )
                  }
                >
                  {policy.filename || "Policy"}
                </button>
                <span className="text-muted">
                  {" "}
                  expires {policy.expiry_date ? moment(policy.expiry_date).format("DD-MM-YYYY") : "unknown"}
                  {policy.uploaded_at ? `, uploaded ${moment(policy.uploaded_at).format("DD-MM-YYYY HH:mm")}` : ""}
                </span>
              </div>
            ))}
          </div>
        )}

        {!status.can_upload && <div className="text-muted">{status.blocked_reason}</div>}

        {status.can_upload && (
          <>
            <div className="form-group">
              <label htmlFor="store-protect-file" className="font-weight-bold">
                Upload the policy the customer sent (PDF, JPG or PNG, up to 10 MB)
              </label>
              <input
                id="store-protect-file"
                key={reload}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                className="form-control-file"
                onChange={handleFile}
                disabled={busy}
              />
              <small className="text-muted">
                It is checked automatically against this order: site address, dates and amount insured.
              </small>
            </div>

            {/* A policy that could not be checked has no date read from it,
                so accepting it anyway always needs one. */}
            {(refusal?.needs_expiry_date || (refusal?.can_override && !check) || expiryDate) && (
              <div className="form-group">
                <label htmlFor="store-protect-expiry" className="font-weight-bold">
                  Date the policy expires
                </label>
                <input
                  id="store-protect-expiry"
                  type="date"
                  className="form-control"
                  style={{ maxWidth: 220 }}
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  disabled={busy}
                />
              </div>
            )}

            {refusal?.can_override && (
              <div className="border rounded p-3 mb-3">
                <div className="font-weight-bold mb-2">
                  {check ? "The policy did not pass the automatic check" : "The policy could not be checked automatically"}
                </div>
                {check?.problems?.length > 0 && (
                  <ul className="mb-2 pl-5">
                    {check.problems.map((problem, index) => (
                      <li key={index}>{problem}</li>
                    ))}
                  </ul>
                )}
                {read && (
                  <div className="text-muted mb-3">
                    Read from the policy: address {read.site_address || "not found"}; from{" "}
                    {read.start_date || "not found"} to {read.end_date || "not found"}; insured for{" "}
                    {read.sum_insured != null ? `£${read.sum_insured}` : "not found"}.
                  </div>
                )}
                <label htmlFor="store-protect-reason" className="font-weight-bold">
                  To accept it anyway, say why
                </label>
                <textarea
                  id="store-protect-reason"
                  className="form-control"
                  rows={2}
                  maxLength={500}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  disabled={busy}
                />
              </div>
            )}

            <div className="d-flex" style={{ gap: 8 }}>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() => submit(false)}
                disabled={busy || !file}
              >
                {busy ? "Checking..." : "Check and upload"}
              </button>
              {refusal?.can_override && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-warning"
                  onClick={() => submit(true)}
                  disabled={busy || !file || !reason.trim()}
                >
                  Upload anyway
                </button>
              )}
            </div>
          </>
        )}

        {message.text && (
          <div className={`mt-3 ${message.type === "success" ? "text-success" : "text-danger"}`}>{message.text}</div>
        )}
      </div>
    </div>
  );
}
